import { formatDDMMYYYY, getDeep, getUserAccess } from '../helpers'
import {
  Additional,
  AdditionalHelp,
  AddressForm,
  BankForm,
  Beziehung,
  Communication,
  EmailEntry,
  FileUpload,
  NaturalPersonData,
  NaturalPersonLegitimation,
  PhoneEntry,
  UrlEntry,
} from '../types/stammdaten.type'

export const emptyNaturalPersonData: NaturalPersonData = {
  birth_date: '',
  birth_name: '',
  full_first_names: '',
  place_of_birth: '',
  country_of_birth: '',
  nationality: [],
}

export const emptyNaturalPersonLegitimation = (): NaturalPersonLegitimation => ({
  comment: '',
  pep: false,
  id_number: '',
  birth_date: '',
  nationality: [],
  place_of_birth: '',
  valid_to: '',
  issued_on: '',
  issuing_authority: '',
})

export const emptyAddress = (): AddressForm => ({
  valid_since: '',
  valid_to: '',
  address_suffix: '',
  type: '',
  zip_code: '',
  city: '',
  country: '',
  shipping_supplement: '',
  abbrev_salutation: '',
  different_person: '',
  delivery_addressee: '',
  note: '',
  use_different_delivery_addressee: false,
  street: '',
  post_office_box: '',
})
export const emptyBank = (): BankForm => ({
  valid_since: '',
  valid_to: '',
  country: '',
  bank_name: '',
  iban: '',
  account_number: '',
  account_holder: '',
  note: '',
})
export const emptyBeziehung = (): Beziehung => ({
  has_is: '',
  name: '',
  addressee_type: '',
  naturliche_person_info: {
    anrede: '',
    academic_degree: '',
    first_name: '',
    name: '',
    birth_date: '',
    federal_state: '',
    eTIN: '',
    id_number: '',
  },
  legal_person_info: {
    anrede: '',
    name: '',
    legal_form: '',
  },
  address: {
    type: '',
    street: '',
    post_office_box: '',
    zip_code: '',
    city: '',
    country: '',
    phone: '',
    email: '',
  },
})

export const makePhone = (): PhoneEntry => ({
  value: '',
  note: '',
  is_default: false,
  is_gl: false,
})
export const makeEmail = (): EmailEntry => ({
  value: '',
  note: '',
  is_default: false,
  is_invoice: false,
})
export const makeUrl = (): UrlEntry => ({ value: '', note: '', is_default: false })

export const makeAdditional = (): AdditionalHelp => ({ type: '', options: [], name: '' })

export const EMPTY_COMMUNICATION: Communication = { phone: [], email: [], url: [] }
export const EMPTY_ADDITIONAL: Additional = { field: [] }

export const toArray = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Object.keys(raw).every(k => /^\d+$/.test(k)))
    return Object.values(raw)
  return raw && typeof raw === 'object' ? [raw] : []
}

export function extractStammdatenRoot(api: any) {
  return (
    api?.client?.stammdaten?.stammdaten ??
    api?.stammdaten?.stammdaten ??
    api?.stammdaten ??
    api?.client?.stammdaten ??
    api
  )
}

export const normalizeAddresses = (resp: any): AddressForm[] => {
  const root = extractStammdatenRoot(resp) || {}
  let raw = root.address
  if (raw && typeof raw === 'object' && Object.keys(raw).every(k => /^\d+$/.test(k)))
    raw = Object.values(raw)
  else if (raw && typeof raw === 'object') raw = [raw]
  else raw = []
  return (raw as any[]).map(r => ({
    ...emptyAddress(),
    valid_since: typeof r?.valid_since === 'string' ? r.valid_since : '',
    valid_to: typeof r?.valid_to === 'string' ? r.valid_to : '',
    address_suffix: r?.address_suffix ?? '',
    type: r?.type ?? '',
    zip_code: r?.zip_code ?? '',
    city: r?.city ?? '',
    country: r?.country ?? '',
    shipping_supplement: r?.shipping_supplement ?? '',
    abbrev_salutation: r?.abbrev_salutation ?? '',
    different_person: r?.different_person ?? '',
    delivery_addressee: r?.delivery_addressee ?? '',
    note: r?.note ?? '',
    use_different_delivery_addressee: !!r?.use_different_delivery_addressee,
    street: r?.street ?? '',
    post_office_box: r?.post_office_box ?? '',
  }))
}

export const normalizeBanks = (resp: any): BankForm[] => {
  const root = extractStammdatenRoot(resp) || {}
  let raw = root.bank
  if (raw && typeof raw === 'object' && Object.keys(raw).every(k => /^\d+$/.test(k)))
    raw = Object.values(raw)
  else if (raw && typeof raw === 'object') raw = [raw]
  else raw = []
  return (raw as any[]).map(r => ({
    ...emptyBank(),
    valid_since: typeof r?.valid_since === 'string' ? r.valid_since : '',
    valid_to: typeof r?.valid_to === 'string' ? r.valid_to : '',
    bank_name: r?.bank_name ?? '',
    iban: r?.iban ?? '',
    account_number: r?.account_number ?? '',
    account_holder: r?.account_holder ?? '',
    country: r?.country ?? '',
    note: r?.note ?? '',
  }))
}

export const normalizeBeziehung = (resp: any): Beziehung[] => {
  const root = extractStammdatenRoot(resp) || {}
  let raw = root.beziehung

  if (raw && typeof raw === 'object' && Object.keys(raw).every(k => /^\d+$/.test(k))) {
    raw = Object.values(raw)
  } else if (raw && typeof raw === 'object') {
    raw = [raw]
  } else {
    raw = []
  }

  return (raw as any[]).map(r => {
    const np = r?.naturliche_person_info || {}
    const lp = r?.legal_person_info || {}
    const ta = r?.tax_authority || {}
    const addr = r?.address || {}

    return {
      ...emptyBeziehung(),
      has_is: r?.has_is ?? r?.has_id ?? '',
      name: r?.name ?? '',
      addressee_type: r?.addressee_type ?? '',
      naturliche_person_info: {
        anrede: np?.anrede ?? '',
        academic_degree: np?.academic_degree ?? '',
        first_name: np?.first_name ?? '',
        name: np?.name ?? '',
        legal_form: np?.legal_form ?? '',
        birth_date: np?.birth_date ?? '',
        federal_state: np?.federal_state ?? '',
        eTIN: np?.eTIN ?? '',
        id_number: np?.id_number ?? '',
      },
      legal_person_info: {
        anrede: lp?.anrede ?? '',
        name: lp?.name ?? '',
        legal_form: lp?.legal_form ?? '',
      },
      tax_authority: {
        country_code: ta?.country_code ?? '',
        tax_authority_name: ta?.tax_authority_name ?? '',
        tax_number: ta?.tax_number ?? '',
        betriebsfinanzamt: ta.betriebsfinanzamt,
        umsatzsteuerfinanzamt: ta.umsatzsteuerfinanzamt,
        lohnsteuerfinanzamt: ta.lohnsteuerfinanzamt,
      },
      address: {
        type: addr?.type ?? '',
        street: addr?.street ?? '',
        post_office_box: addr?.post_office_box ?? '',
        zip_code: addr?.zip_code ?? '',
        city: addr?.city ?? '',
        country: addr?.country ?? '',
        phone: addr?.phone ?? '',
        email: addr?.email ?? '',
      },
    }
  })
}

export const normalizeCommunication = (resp: any, ensureOneRow = false): Communication => {
  const root = extractStammdatenRoot(resp) || {}
  const c = root.communication || {}

  const mapPhone = (x: any): PhoneEntry => ({
    value: x?.value ?? '',
    note: x?.note ?? '',
    is_default: !!x?.is_default,
    is_gl: !!x?.is_gl,
  })
  const mapEmail = (x: any): EmailEntry => ({
    value: x?.value ?? '',
    note: x?.note ?? '',
    is_default: !!x?.is_default,
    is_invoice: !!x?.is_invoice,
  })
  const mapUrl = (x: any): UrlEntry => ({
    value: x?.value ?? '',
    note: x?.note ?? '',
    is_default: !!x?.is_default,
  })

  let phone = toArray(c.phone).map(mapPhone)
  let email = toArray(c.email).map(mapEmail)
  let url = toArray(c.url).map(mapUrl)

  if (ensureOneRow) {
    if (phone.length === 0) phone = [makePhone()]
    if (email.length === 0) email = [makeEmail()]
    if (url.length === 0) url = [makeUrl()]
  }

  return { phone, email, url }
}

export const normalizeAdditional = (resp: any, ensureOneRow = false): Additional => {
  const root = extractStammdatenRoot(resp) || {}
  const c = root.additional || {}

  const fields = toArray(c.field).map((x: any) => ({
    type: x?.type ?? '',
    name: x?.name ?? '',
    options: toArray(x?.options).map((o: any) => ({
      label: o?.label ?? '',
      value: o?.value ?? '',
    })),
  }))

  return {
    field: fields.length ? fields : ensureOneRow ? [{ type: '', name: '', options: [] }] : [],
  }
}

export const normalizeFiles = (resp: any, type: string): FileUpload[] => {
  const root = extractStammdatenRoot(resp) || {}
  const c = (type === 'files' ? root.files : root.filesClient) || {}

  const fields = toArray(c).map((x: any) => ({
    name: x?.name ?? '',
    type: x?.type ?? '',
    date: x?.date ?? '',
    user: x?.user ?? '',
  }))

  return fields
}

export const extractDynamicAdditional = (api: any) => {
  return (
    getDeep(api, 'client.stammdaten.additional.dynamic') ??
    getDeep(api, 'stammdaten.additional.dynamic') ??
    getDeep(api, 'stammdaten.additional.dynamic') ??
    getDeep(api, 'additional.dynamic') ??
    {}
  )
}

export const createFieldUpdate = (obj: any, path: string, value: any): void => {
  const keys = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  if (!keys.length) return

  let cur = obj
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    if (cur[k] === undefined) {
      cur[k] = {}
    }
    cur = cur[k]
  }

  const last = keys[keys.length - 1]
  cur[last] = value
}

export const uploadStammdatenFile = async (params: {
  file: File
  clientId: string
  type: string
  name: string
  email?: string
}): Promise<FileUpload> => {
  const { file, clientId, type, name, email } = params

  const formData = new FormData()
  formData.append('clientId', clientId)
  formData.append('type', `stammdaten_${type}`)
  formData.append('file', file)
  formData.append('firmename', name)

  const res = await fetch(`${process.env.REACT_APP_API_URL}/files/kyc/upload`, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) throw new Error('Upload error')

  return {
    name: file.name,
    type: 'user',
    date: new Date().toISOString(),
    user: email ? email : getUserAccess().name,
  }
}

export const getCountryRiskColor = (
  country: string,
  countryRisks: Record<string, number | null>
) => {
  const risk = Number(countryRisks[country])
  if (risk === 3) return 'border border-danger text-danger px-2'
  if (risk === 2) return 'border border-warning-country text-warning-country px-2'
  if (risk === 1) return 'border border-success text-success rounded px-2'
  return ''
}

export const isLastCheckOlderThanOneYear = (lastCheck: string | undefined): boolean => {
  if (!lastCheck) return true
  const parts = lastCheck.split(' ')[0].split('.')
  if (parts.length !== 3) return true
  const [day, month, year] = parts.map(Number)
  const lastCheckDate = new Date(year, month - 1, day)
  if (isNaN(lastCheckDate.getTime())) return true
  const oneYearAgo = new Date()
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
  return lastCheckDate < oneYearAgo
}

export const getKycCheckVariant = (
  lastCheck: string | undefined,
  risk: boolean | undefined
): string => {
  if (!lastCheck) return 'danger'
  if (isLastCheckOlderThanOneYear(lastCheck)) return 'danger'
  return 'success'
}

export function isValidVatDE(value: string): boolean {
  if (!/^[0-9]{9}$/.test(value)) return false

  const digits = value.split('').map(Number)
  let product = 10

  for (let i = 0; i < 8; i++) {
    let sum = (digits[i] + product) % 10
    if (sum === 0) sum = 10
    product = (2 * sum) % 11
  }

  let checkDigit = 11 - product
  if (checkDigit === 10) return false
  if (checkDigit === 11) checkDigit = 0

  return checkDigit === digits[8]
}

export interface TopographCompany {
  legalName?: string
  countryCode?: string
  legalAddress?: { addressLine1?: string; city?: string; postalCode?: string; countryCode?: string }
  identifiers?: { registerNumber?: string; registerCourt?: string; registerType?: string }
  legalForm?: { localName?: string }
  registrationDate?: string
}

type SelectOption = { value: string; label: string }

const REGISTER_TYPE_TO_DATEV: Record<string, string> = {
  HAN: 'HAN',
  HRA: 'HAN',
  HRB: 'HAN',
  HANDELSREGISTER: 'HAN',
  GRB: 'GRB',
  GB: 'GRB',
  GRUNDBUCH: 'GRB',
  GEN: 'GEN',
  GNR: 'GEN',
  GENOSSENSCHAFTSREGISTER: 'GEN',
  VER: 'VER',
  VR: 'VER',
  VEREINSREGISTER: 'VER',
  PAR: 'PAR',
  PR: 'PAR',
  PARTNERSCHAFTSREGISTER: 'PAR',
  GES: 'GES',
  GSR: 'GES',
  GESELLSCHAFTSREGISTER: 'GES',
}

const toDatevRegisterType = (registerType?: string): string => {
  if (!registerType) return ''
  return REGISTER_TYPE_TO_DATEV[registerType.trim().toUpperCase()] ?? registerType
}

// Legal form option labels are "<short_name> <nation>" (e.g. "GmbH DE"). The
// Topograph localName carries the abbreviation in parentheses (e.g.
// "Gesellschaft mit beschränkter Haftung (GmbH)") — extract it and prefer the
// option whose nation matches the company's country, else the first by short name.
const resolveLegalForm = (
  localName: string | undefined,
  countryCode: string,
  legalForms: SelectOption[]
): string => {
  const match = localName?.match(/\(([^)]+)\)\s*$/)
  if (!match) return ''
  const short = match[1].trim()
  const prefix = `${short} `

  let fallback = ''
  for (const opt of legalForms) {
    if (!opt.label.startsWith(prefix)) continue
    if (countryCode && opt.label === `${short} ${countryCode}`) return opt.value
    if (!fallback) fallback = opt.value
  }
  return fallback
}

// Flatten the Topograph company datapoint into Stammdaten form field paths.
export const mapTopographCompany = (
  company: TopographCompany | null | undefined,
  legalForms: SelectOption[]
): Record<string, string> => {
  if (!company) return {}

  const addr = company.legalAddress ?? {}
  const ids = company.identifiers ?? {}
  const countryCode = addr.countryCode ?? company.countryCode ?? ''

  const mapped: Record<string, string> = {
    name: company.legalName ?? '',
    legal_form: resolveLegalForm(company.legalForm?.localName, countryCode, legalForms),
    'address.0.street': addr.addressLine1 ?? '',
    'address.0.city': addr.city ?? '',
    'address.0.zip_code': addr.postalCode ?? '',
    'address.0.country': countryCode,
    location_of_headquarters: addr.city ?? '',
    country_of_headquarters: countryCode,
    'registry_court.register_number': ids.registerNumber ?? '',
    'registry_court.commercial_register': ids.registerCourt ?? '',
    'registry_court.register_entry': toDatevRegisterType(ids.registerType),
    founding_date: company.registrationDate ? formatDDMMYYYY(company.registrationDate) : '',
  }

  // Register fields are gated on nationality in the UI; set it when the country
  // matches a nationality option (DE/AT) so the prefilled values are visible.
  if (countryCode === 'DE' || countryCode === 'AT') {
    mapped.nationality = countryCode
  }

  return mapped
}
